import { api, ApiError } from './api';
import {
  DeliveryFeeConfig,
  DeliveryFeeConfigInput,
  DeliveryMargin,
  RecoveryProvider,
  RecoveryProviderInput,
  ZofeurConfig,
  ZofeurConfigInput,
  ZofeurZone,
  ZONE_LABELS,
} from '../types/delivery';

/**
 * Spec 053 Flow F6 — delivery-fee config + recovery-provider registry, served by the **orders**
 * service under `/api/admin/delivery/**` (gateway route `orders-admin-delivery`, screen `finance`;
 * #1113: `/margin/**` needs `finance-config`, it is shown on Finance → Configuration).
 *
 * Providers are soft-deleted (`hiddenAt`), never hard-deleted, so `remove` is a DELETE that the
 * backend turns into a hide; the row keeps every trip it was ever snapshotted onto (BR-26).
 */
export const deliveryConfigApi = {
  getConfig: (token: string) =>
    api.get<DeliveryFeeConfig>('/api/admin/delivery/config', token),

  /**
   * **400** on a negative or missing amount, with the offending field named — bean validation on
   * the request body (`@DecimalMin("0.00")`), not a 422: a negative amount is a malformed field,
   * and 422 is reserved for rule violations. That is the only request-time rejection.
   *
   * BR-14 is not checked here either: it is a **startup** guard (`PlatformFeeGuard` in the orders
   * service), which refuses to boot when `garagat.delivery.enabled=true` and the PLT series is off.
   * Saving a non-zero fee while the series is off is therefore accepted by this endpoint.
   */
  updateConfig: (input: DeliveryFeeConfigInput, token: string) =>
    api.put<DeliveryFeeConfig>('/api/admin/delivery/config', input, token),

  /** Spec 072 v2 — the Zofeur option's price, free-above and emirates. All null until the first save. */
  getZofeurConfig: (token: string) =>
    api.get<ZofeurConfig>('/api/admin/delivery/zofeur-config', token),

  /**
   * 422 with one sentence (design §3): a price outside 1–10,000 or not whole dirhams, a free-above
   * outside 0–1,000,000, or no emirate; #1254 adds the provider form's VAT, cost and insurance rules.
   * Audited as `DELIVERY_CONFIG_UPDATED` with before and after. The server keeps the hidden Zofeur
   * provider row (what a Zofeur leg is assigned) in step with the card. Spec 080: `zonePrices` is
   * passed through as is (blanks already omitted); a bad one is a 422 naming the zone, which
   * `toValidationIssues` places under that zone's row.
   */
  updateZofeurConfig: (input: ZofeurConfigInput, token: string) =>
    api.put<ZofeurConfig>('/api/admin/delivery/zofeur-config', input, token),

  /**
   * The Recovery registry. #1254: never Zofeur — `includeZofeur` adds the Zofeur card's provider row,
   * for the provider-settlement picker, where Zofeur trips are paid.
   */
  listProviders: (token: string, options: { includeZofeur?: boolean } = {}) =>
    api.get<RecoveryProvider[]>(
      options.includeZofeur ? '/api/admin/delivery/providers?includeZofeur=true' : '/api/admin/delivery/providers',
      token,
    ),

  /**
   * 422 for a contractor with neither a TRN nor the "not VAT registered" opt-out. #1254: the
   * registry is Recovery only.
   */
  createProvider: (input: RecoveryProviderInput, token: string) =>
    api.post<RecoveryProvider>('/api/admin/delivery/providers', input, token),

  updateProvider: (id: string, input: RecoveryProviderInput, token: string) =>
    api.put<RecoveryProvider>(`/api/admin/delivery/providers/${encodeURIComponent(id)}`, input, token),

  /** Soft delete → 204. A provider already snapshotted onto a trip stays intact on that trip. */
  removeProvider: (id: string, token: string) =>
    api.delete<void>(`/api/admin/delivery/providers/${encodeURIComponent(id)}`, token),

  /**
   * Flow F6 / AC-41 — month-to-date delivery margin. Read-only and derived; there is nothing to
   * configure here. Fails independently of the config and registry loads above, so a margin the
   * orders service cannot compute leaves the rest of the tab usable.
   */
  getMargin: (token: string) =>
    api.get<DeliveryMargin>('/api/admin/delivery/margin', token),
};

/** One inline message; `field` is null when it belongs above the form rather than under an input. */
export interface ValidationIssue {
  field: string | null;
  message: string;
}

/**
 * Where a rejected save belongs on screen.
 *
 * Two server shapes reach us through `api.ts`:
 *  - bean validation (400) — `{message, errors:{field:msg}}`, which `api.ts` flattens into
 *    `"…  (name: must not be blank; trn: …)"`. We unflatten it back to per-field issues.
 *  - business rules (422) — a bare sentence such as "A UAE TRN is 15 digits".
 *    Those carry no field, so they are matched to the input an admin would go and fix; anything
 *    unrecognised stays a form-level message rather than being hidden under the wrong label.
 *
 * Anything that is not a 4xx validation failure returns `[]` — the caller shows it as an error
 * banner, because a 500 or a dropped connection is not the admin's typing.
 */
export function toValidationIssues(err: unknown): ValidationIssue[] {
  if (!(err instanceof ApiError)) return [];
  if (err.status !== 400 && err.status !== 422) return [];

  const flattened = /\(([^()]*:[^()]*)\)\s*$/.exec(err.message);
  if (flattened) {
    const issues = flattened[1]
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const at = part.indexOf(':');
        return { field: part.slice(0, at).trim(), message: part.slice(at + 1).trim() };
      })
      .filter((issue) => issue.field && issue.message);
    if (issues.length > 0) return issues;
  }

  return [{ field: fieldForMessage(err.message), message: err.message }];
}

/** Spec 080 — the input a zone's price issue belongs under. */
export function zonePriceField(zone: ZofeurZone): string {
  return `zonePrice:${zone}`;
}

function fieldForMessage(message: string): string | null {
  const m = message.toLowerCase();
  // Spec 080 (DD11) — "Enter a price for {Zone} between AED 1 and AED 10,000, …" belongs to that zone's row.
  const zoneMatch = /^enter a price for (.+?) between aed 1 and aed 10,000/i.exec(message.trim());
  if (zoneMatch) {
    const label = zoneMatch[1].trim().toLowerCase();
    const zone = (Object.keys(ZONE_LABELS) as ZofeurZone[]).find((z) => ZONE_LABELS[z].toLowerCase() === label);
    if (zone) return zonePriceField(zone);
  }
  // Spec 072 v2 — the Zofeur card's three 422s (its fields are `price`, `threshold`, `emirates`).
  if (m.includes('zofeur price')) return 'price';
  if (m.includes('free-above')) return 'threshold';
  if (m.includes('emirate')) return 'emirates';
  if (m.includes('trn') || m.includes('vat regist')) return 'trn';
  // Spec 072 — before "disclaimer": "Enter the insured amount before showing the disclaimer" is the amount's.
  if (m.includes('insured amount')) return 'insuredAmount';
  if (m.includes('disclaimer')) return 'disclaimer';
  if (m.includes('threshold')) return 'threshold';
  if (m.includes('fee per trip') || m.includes('feepertrip')) return 'feePerTrip';
  if (m.includes('cost per trip') || m.includes('costpertrip')) return 'costPerTrip';
  if (m.includes('name')) return 'name';
  return null;
}

/** The message for one field, or undefined. */
export function issueFor(issues: ValidationIssue[], field: string): string | undefined {
  return issues.find((i) => i.field === field)?.message;
}

/** Messages with no home under an input — rendered as the form-level banner. */
export function formIssues(issues: ValidationIssue[]): string[] {
  return issues.filter((i) => i.field === null).map((i) => i.message);
}
